import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Card, Col, Row, Button } from "reactstrap";
import Pagination, { bootstrap5PaginationPreset } from "react-responsive-pagination";
import { useTranslation } from "react-i18next";
import { getAllBrokers } from "services/services";

// Default avatar component to prevent multiple requests
const AgentAvatar = ({ src, alt, style }) => {
  const [imageSrc, setImageSrc] = useState(src || "/default.png");
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setImageSrc(src || "/default.png");
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      setImageSrc("/default.png");
    }
  };

  return (
    <img
      src={imageSrc}
      alt={alt}
      style={style}
      onError={handleError}
    />
  );
};

const BrokerAgents = () => {
  const { t } = useTranslation();
  const { agentId } = useParams();
  const history = useHistory();

  const [searchTerm, setSearchTerm] = useState("");
  const timeoutRef = useRef(null);

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const perPage = 10;

  const fetchAgents = useCallback(
    async (page, pageSize, search) => {
      setLoading(true);
      try {
        const res = await getAllBrokers(page, pageSize, search || "", "", "", "");
        const brokers = res?.data?.items || [];
        const broker = brokers.find((b) => b._id === agentId || b.id === agentId);

        if (broker?.agents?.length) {
          setAgents(broker.agents);
          setTotalPages(Math.ceil(broker.agents.length / perPage));
        } else {
          setAgents([]);
          setTotalPages(0);
        }
      } catch (error) {
        console.error(error);
        setAgents([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [agentId, perPage]
  );

  useEffect(() => {
    fetchAgents(currentPage, perPage, searchTerm);
  }, [fetchAgents, currentPage, searchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      fetchAgents(1, perPage, value);
    }, 500);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Memoize paginated agents to prevent unnecessary re-renders
  const paginatedAgents = useMemo(() => {
    return agents.slice((currentPage - 1) * perPage, currentPage * perPage);
  }, [agents, currentPage, perPage]);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col lg={12}>
            <Card className="card-transparent">
              <div className="mb-3">
                <Row className="d-flex align-items-center">
                  <Col lg={6}>
                    <h5 className="text-color-gredient">{t("Broker Agents")}</h5>
                  </Col>
                  <Col lg={6} className="d-flex justify-content-end gap-2">
                    {/* <div className="common-filters search-box me-2">
                      <input
                        id="agent-search"
                        type="search"
                        className="form-control"
                        placeholder={`${t("Search")}...`}
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                    </div> */}

                    <Button
                      className="btn all-common-btn"
                      outline
                      onClick={() => history.push(`/admin/brokers/${agentId}`)}
                    >
                      {t("Back to Details")}
                    </Button>
                  </Col>
                </Row>
              </div>

              <div className="table-responsive custom-responsive-table">
                <table className="table common-table table-nowrap table-bordered">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">{t("Photo")}</th>
                      <th scope="col">{t("Name")}</th>
                      <th scope="col">{t("Email")}</th>
                      <th scope="col">{t("Phone Number")}</th>
                      <th scope="col">{t("Created At")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          {t("Loading...")}
                        </td>
                      </tr>
                    ) : agents.length > 0 ? (
                      paginatedAgents.map((agent, idx) => (
                        <tr key={agent._id || idx}>
                          <td>{(currentPage - 1) * perPage + idx + 1}</td>
                          <td>
                            <AgentAvatar
                              src={agent.profilePicture}
                              alt="Agent"
                              style={{ width: 50, height: 50, borderRadius: "50%" }}
                            />
                          </td>
                          <td>{agent.name || "-"}</td>
                          <td>{agent.email || "-"}</td>
                          <td>{agent.phone_number || "-"}</td>
                          <td>{agent.createdAt || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          {t("No Agents Found")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <Row className="mt-2">
                  <Col md={9}></Col>
                  <Col md={3}>
                    <Pagination
                      {...bootstrap5PaginationPreset}
                      current={currentPage}
                      total={totalPages}
                      onChange={handlePageChange}
                      onPageChange={handlePageChange}
                      className="pagination justify-content-end"
                    />
                  </Col>
                </Row>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default BrokerAgents;
